import type { ReactNode } from 'react'
import { useTranslation } from '@pancakeswap/localization'
import { NextLinkFromReactRouter, Skeleton } from '@pancakeswap/uikit'
import { vars } from '@pancakeswap/ui/css/vars.css'
import { CadinuLevelNftsAbi } from 'config/abi/cadinuLevelNfts'
import { cadinuProfileAbi } from 'config/abi/cadinuProfile'
import { useEffect, useState } from 'react'
import { useProfile } from 'state/profile/hooks'
import styled from 'styled-components'
import { getCadinuProfileAddress } from 'utils/addressHelpers'
import { formatUnits } from 'viem'
import { useAccount, useContractRead } from 'wagmi'

/**
 * Home hero, right column — Bez's reference mock (apps-dex/design/reference-home-cards-bez-20260928.png):
 * three cards visible (centre upright, raised and a little larger; left/right tilted ±10°), thin light
 * border, soft glow; "Your CADINU identity", a pill "Create your profile" and "Profile optional.
 * Explore and swap without one." The deck rotates every 2.6 s (centre → left, left → back, a back card
 * enters right, 1000 ms ease, every 4 s), so all six characters pass through within one cycle; it stops under
 * prefers-reduced-motion (static three) and while the tab is hidden. The cards are project-owned
 * intro art (`/public/images/brand/nft/PROVENANCE.md`) — never the user's NFTs, never a reward. The
 * profile button opens the EXISTING `/create-profile` flow; a profile is never a precondition for
 * Swap, which stays the page's primary CTA.
 */

const HERO_FG = '#F4F6FF' // snow — 18.6:1 on night
const HERO_FG_2 = '#C9D2E6' // snow-2 — 13.2:1 on night
const HERO_MUTED = '#93A3BF' // muted — 7.9:1 on night

/** Order = hand order, left to right. The name is the alt text and the card's label. */
export const HUSKIES = [
  { id: 'artist', name: 'Artist' },
  { id: 'business', name: 'Business' },
  { id: 'cowboy', name: 'Cowboy' },
  { id: 'loyalty', name: 'Loyalty' },
  { id: 'futuristic', name: 'Futuristic' },
  { id: 'hunter', name: 'Hunter' },
] as const

// Six characters, three "seats" in front (0 left, 1 centre, 2 right) and three parked behind the
// centre card (3 right-back, 4 centre-back, 5 left-back). Card i sits in seat (i − offset) mod 6;
// each tick advances offset, so centre → left, left → back, right → centre, right-back → right.
type Seat = { x: number; y: number; rot: number; scale: number; z: number; opacity: number }
const SEATS: Seat[] = [
  { x: -0.82, y: 0.08, rot: -10, scale: 0.94, z: 4, opacity: 1 },
  { x: 0, y: -0.04, rot: 0, scale: 1, z: 6, opacity: 1 },
  { x: 0.82, y: 0.08, rot: 10, scale: 0.94, z: 5, opacity: 1 },
  { x: 0.3, y: 0.02, rot: 6, scale: 0.86, z: 3, opacity: 0 },
  { x: 0, y: 0, rot: 0, scale: 0.84, z: 2, opacity: 0 },
  { x: -0.3, y: 0.02, rot: -6, scale: 0.86, z: 1, opacity: 0 },
]
const FRONT = 3
const TICK_MS = 4000
const MOVE_MS = 1000
// footprint in card-widths: left/right seats ±0.82 plus the tilted card's extent
const FAN_W = 2.75
const FAN_H = 1.6


const Wrap = styled.div`
  --card-w: clamp(96px, 31vw, 150px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
  min-width: 0;

  ${({ theme }) => theme.mediaQueries.md} {
    --card-w: clamp(110px, 13vw, 176px);
    gap: 24px;
  }
`

/* Footprint = the three front seats (FAN_W × FAN_H card-widths); the parked cards sit behind the centre. */
const Fan = styled.div`
  position: relative;
  width: calc(var(--card-w) * ${FAN_W});
  height: calc(var(--card-w) * ${FAN_H});
  max-width: 100%;
`

const Card = styled.figure<{ $seat: number; $animate: boolean }>`
  position: absolute;
  left: 50%;
  top: 50%;
  margin: 0;
  width: var(--card-w);
  height: calc(var(--card-w) * 1.4);
  transform: translate(
      calc(-50% + var(--card-w) * ${({ $seat }) => SEATS[$seat].x}),
      calc(-50% + var(--card-w) * ${({ $seat }) => SEATS[$seat].y})
    )
    rotate(${({ $seat }) => SEATS[$seat].rot}deg) scale(${({ $seat }) => SEATS[$seat].scale});
  z-index: ${({ $seat }) => SEATS[$seat].z};
  opacity: ${({ $seat }) => SEATS[$seat].opacity};
  visibility: ${({ $seat }) => ($seat < FRONT ? 'visible' : 'hidden')};
  transition: ${({ $animate }) =>
    $animate
      ? `transform ${MOVE_MS}ms cubic-bezier(0.22, 1, 0.36, 1), opacity ${MOVE_MS * 0.6}ms ease, visibility 0s linear ${MOVE_MS}ms`
      : 'none'};
  will-change: transform, opacity;
  border-radius: 14px;
  overflow: hidden;
  background: #0a0e22;
  border: 1px solid rgba(220, 232, 255, 0.55);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.35), 0 0 0 1px rgba(127, 212, 232, 0.12),
    0 0 40px rgba(127, 212, 232, 0.16), 0 16px 40px rgba(0, 0, 0, 0.6);

  ${({ $seat, $animate }) => ($seat < FRONT && $animate ? 'transition-delay: 0s, 0s, 0s;' : '')}

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  figcaption {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: calc(var(--card-w) * 0.22) 0 calc(var(--card-w) * 0.07);
    text-align: center;
    font-family: ${vars.fonts.display};
    font-weight: 700;
    font-size: clamp(9px, calc(var(--card-w) * 0.1), 13px);
    line-height: 1.2;
    letter-spacing: 0.02em;
    color: ${HERO_FG};
    background: linear-gradient(180deg, rgba(5, 7, 19, 0) 0%, rgba(5, 7, 19, 0.85) 60%);
  }
`

const Headline = styled.h2`
  margin: 0;
  font-family: ${vars.fonts.display};
  font-weight: 800;
  font-size: 24px;
  line-height: 30px;
  letter-spacing: -0.01em;
  color: ${HERO_FG};
  text-align: center;

  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 28px;
    line-height: 34px;
  }
`

/* The mock's pill: ice fill, night text — same fill as "Swap now", which stays first and primary. */
const PillLink = styled(NextLinkFromReactRouter)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  min-width: 44px;
  padding: 0 32px;
  border-radius: 999px;
  font-family: ${vars.fonts.normal};
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: #050713;
  background: #7fd4e8;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.35), 0 8px 24px rgba(127, 212, 232, 0.25);
  transition: transform 0.2s ease-out, box-shadow 0.2s ease-out;

  &:hover {
    transform: translateY(-1px);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.35), 0 10px 28px rgba(127, 212, 232, 0.35);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 1px #7e00fe, 0 0 0 4px rgba(126, 0, 254, 0.45);
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover {
      transform: none;
    }
  }
`

const ProfileBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  max-width: 360px;
  text-align: center;

  p {
    margin: 0;
    font-family: ${vars.fonts.normal};
    font-size: 15px;
    line-height: 22px;
    color: ${HERO_FG_2};
  }
  small {
    display: block;
    font-size: 12px;
    line-height: 16px;
    color: ${HERO_MUTED};
  }
`

/* One focusable element per action (a link styled as a secondary button), so the tab order is
   Swap now → Add liquidity → Create your profile / View profile with no nested button-in-link. */
const SecondaryLink = styled(NextLinkFromReactRouter)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  min-width: 44px;
  padding: 0 20px;
  border-radius: 14px;
  font-family: ${vars.fonts.normal};
  font-weight: 700;
  font-size: 15px;
  line-height: 20px;
  color: ${HERO_FG};
  background: transparent;
  border: 1px solid rgba(180, 200, 255, 0.35);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
  transition: border-color 0.2s ease-out;

  &:hover {
    border-color: #7fd4e8;
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 1px #7e00fe, 0 0 0 4px rgba(126, 0, 254, 0.45);
  }
`

const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px 10px 10px;
  border-radius: 999px;
  background: rgba(10, 14, 34, 0.78);
  border: 1px solid rgba(180, 200, 255, 0.18);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);

  img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    background: #0a0e22;
  }
  strong {
    font-family: ${vars.fonts.display};
    font-weight: 700;
    font-size: 16px;
    line-height: 20px;
    color: ${HERO_FG};
  }
`

const shortAddress = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`

const CostLines = styled.ul`
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
  font-size: 12px;
  line-height: 18px;
  color: ${HERO_MUTED};

  li {
    margin: 0;
  }
  strong {
    color: ${HERO_FG_2};
    font-weight: 600;
  }
`

const RetryButton = styled.button`
  min-height: 44px;
  min-width: 44px;
  padding: 0 16px;
  border-radius: 14px;
  border: 1px solid rgba(180, 200, 255, 0.35);
  background: transparent;
  color: ${HERO_FG};
  font-family: ${vars.fonts.normal};
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 1px #7e00fe, 0 0 0 4px rgba(126, 0, 254, 0.45);
  }
`

/**
 * Live starter-NFT price: the same two reads the existing flow makes (`ProfileCreation/Mint.tsx`):
 * profile.getNftAddressesForLevel(1) → first collection → priceInCbon. Never a guessed amount:
 * loading → skeleton, any failure → "price unavailable".
 */
const useStarterNftPrice = () => {
  const collections = useContractRead({
    address: getCadinuProfileAddress(),
    abi: cadinuProfileAbi,
    functionName: 'getNftAddressesForLevel',
    args: [1n],
  })
  const first = collections.data?.[0]
  const price = useContractRead({
    address: first,
    abi: CadinuLevelNftsAbi,
    functionName: 'priceInCbon',
    enabled: Boolean(first),
  })
  const isLoading = collections.isLoading || (Boolean(first) && price.isLoading)
  const isError = collections.isError || price.isError || (collections.isSuccess && !first)
  const cbon = price.data !== undefined ? formatUnits(price.data as bigint, 18) : undefined
  return { isLoading, isError, cbon }
}

const formatCbon = (v: string) => {
  const n = Number(v)
  return Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 2 }) : v
}

const CostNote = () => {
  const { t } = useTranslation()
  const { isLoading, isError, cbon } = useStarterNftPrice()
  return (
    <CostLines aria-label={t('What creating a profile costs')} data-testid="profile-cost">
      <li data-testid="starter-price" data-state={isLoading ? 'loading' : isError || !cbon ? 'error' : 'ready'}>
        {t('Starter NFT')}:{' '}
        {isLoading ? (
          <Skeleton width={90} height={14} display="inline-block" />
        ) : isError || !cbon ? (
          <strong>{t('price unavailable')}</strong>
        ) : (
          <strong>{t('%amount% CBON', { amount: formatCbon(cbon) })}</strong>
        )}
      </li>
      <li>{t('Profile creation: no extra fee')}</li>
      <li>{t('Network gas: paid in BNB')}</li>
    </CostLines>
  )
}

/**
 * `gate` (2026-09-29): the wallet-gated pages (create-profile, profile, NFT buy) reuse the same card
 * fan as a friendly "connect first" screen: the profile blocks are replaced by the gate's text + action.
 */
export const IdentityCards = ({ gate }: { gate?: { text: string; action: ReactNode } } = {}) => {
  const { t } = useTranslation()
  const { address: account } = useAccount()
  const { profile, hasProfile, isInitialized, fetchFailed, refresh } = useProfile()
  // profile state comes from the wallet + localStorage cache: client-only, so it never hydrates
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  const connected = mounted && Boolean(account)
  // connected: loading → skeleton (no CTA) | error → retry (no NFT-buy invitation) | has profile | none
  const state: 'loading' | 'error' | 'has-profile' | 'no-profile' = !connected
    ? 'no-profile'
    : !isInitialized
    ? 'loading'
    : fetchFailed
    ? 'error'
    : hasProfile
    ? 'has-profile'
    : 'no-profile'

  // deck rotation: reduced-motion → static three; hidden tab → paused
  const [offset, setOffset] = useState(0)
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const sync = () => setReduced(mq.matches)
    sync()
    mq.addEventListener?.('change', sync)
    return () => mq.removeEventListener?.('change', sync)
  }, [])
  useEffect(() => {
    if (reduced) return undefined
    let timer: ReturnType<typeof setInterval> | undefined
    const start = () => {
      if (!timer) timer = setInterval(() => setOffset((o) => (o + 1) % HUSKIES.length), TICK_MS)
    }
    const stop = () => {
      if (timer) clearInterval(timer)
      timer = undefined
    }
    const onVisibility = () => (document.hidden ? stop() : start())
    onVisibility()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [reduced])
  const seatOf = (i: number) => (i - offset + HUSKIES.length) % HUSKIES.length

  return (
    <Wrap data-testid="identity-cards" data-rotation={reduced ? 'static' : 'rotating'} data-offset={offset}>
      <Fan role="group" aria-label={t('The six CADINU characters')}>
        {HUSKIES.map((h, i) => (
          <Card key={h.id} $seat={seatOf(i)} $animate={mounted && !reduced} data-seat={seatOf(i)} data-card={h.id}>
            <picture>
              <source srcSet={`/images/brand/nft/${h.id}.webp`} type="image/webp" />
              <img
                src={`/images/brand/nft/${h.id}.jpg`}
                alt={t('%name% husky, CADINU character', { name: h.name })}
                width={360}
                height={504}
                loading="eager"
                decoding="async"
              />
            </picture>
            <figcaption aria-hidden>{h.name}</figcaption>
          </Card>
        ))}
      </Fan>
      <Headline id="identity-title">{t('Your CADINU identity')}</Headline>
      {gate && (
        <ProfileBlock data-testid="wallet-gate" data-state="gate">
          <p>{gate.text}</p>
          {gate.action}
        </ProfileBlock>
      )}
      {!gate && state === 'loading' && (
        <ProfileBlock data-testid="profile-block" data-state="loading" aria-busy>
          <Skeleton width={200} height={44} borderRadius="999px" />
          <Skeleton width={260} height={18} />
        </ProfileBlock>
      )}
      {!gate && state === 'error' && (
        <ProfileBlock data-testid="profile-block" data-state="error" role="alert">
          <p>{t("Couldn't load your profile.")}</p>
          <RetryButton type="button" onClick={() => refresh()} data-testid="profile-retry">
            {t('Retry')}
          </RetryButton>
        </ProfileBlock>
      )}
      {!gate && state === 'has-profile' && (
        <ProfileBlock data-testid="profile-block" data-state="has-profile">
          <Identity>
            <img
              src={profile?.nft?.image?.thumbnail ?? '/images/nfts/no-profile-md.png'}
              alt=""
              width={40}
              height={40}
            />
            <strong>{profile?.username || shortAddress(account)}</strong>
          </Identity>
          <SecondaryLink to="/u" data-testid="view-profile">
            {t('View profile')}
          </SecondaryLink>
        </ProfileBlock>
      )}
      {!gate && state === 'no-profile' && (
        <ProfileBlock data-testid="profile-block" data-state="no-profile">
          <PillLink to="/u?onchain=1" data-testid="create-profile">
            {t('Create your profile')}
          </PillLink>
          <p>{t('Profile optional. Explore and swap without one.')}</p>
          <CostNote />
        </ProfileBlock>
      )}
    </Wrap>
  )
}
